import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FcGoogle } from "react-icons/fc";
import { updateAppointment, fetchAppointmentsByPhone } from '../../../../redux/slices/appointmentsSlice';
import { sendPushNotification } from '../../../../redux/slices/sendPushNotification';
import { addNotification } from '../../../../redux/slices/notificationsSlice';
import { fetchPushToken } from '../../../../redux/slices/pushTokensSlice';
import { fetchBusinessLocationByUid } from '../../../../redux/slices/businessLocationSlice';
import { fetchPublicMaster } from '../../../../redux/slices/masterSlice';
import { fetchCabinetClientPackages, fetchClientPackages } from '../../../../redux/slices/clientPackagesSlice';
import QuickBookFAB from '../QuickBookFAB/QuickBookFAB';
import { createBookingHandoff } from '../../../../redux/slices/userSessionSlice';

const EMPTY = [];

const getSelectedServices = (appointment) => {
  const selectedServices = appointment?.selectedServices;

  if (Array.isArray(selectedServices)) {
    return selectedServices;
  }

  if (typeof selectedServices === 'string') {
    try {
      const parsed = JSON.parse(selectedServices);
      return Array.isArray(parsed) ? parsed : EMPTY;
    } catch {
      return EMPTY;
    }
  }

  return EMPTY;
};

const getAppointmentTotalPrice = (appointment) => {
  const customTotal = Number(appointment?.totalPrice);
  if (
    appointment?.totalPrice !== null &&
    appointment?.totalPrice !== undefined &&
    String(appointment.totalPrice).trim() !== '' &&
    Number.isFinite(customTotal) &&
    customTotal >= 0
  ) {
    return customTotal;
  }

  // В price уже хранится итоговая цена позиции с учётом quantity.
  const base = getSelectedServices(appointment).reduce(
    (total, service) => total + (Number(service?.price ?? service?.originalPrice ?? 0) || 0),
    0
  );
  const discountAmount = Number(appointment?.discountAmount) || 0;
  const discountPercent = Number(appointment?.discountedPrice) || 0;

  if (discountAmount > 0) return Math.max(0, base - discountAmount);
  if (discountPercent > 0) return Math.max(0, base * (1 - discountPercent / 100));

  return base;
};

const normalizephoneContact = (phoneContact) => {
  if (!phoneContact) return null;

  let normalizedNumber = String(phoneContact).trim().replace(/\s|-/g, '');

  if (normalizedNumber.startsWith('05')) {
    normalizedNumber = '+972' + normalizedNumber.substring(1);
  } else if (normalizedNumber.startsWith('972')) {
    normalizedNumber = '+' + normalizedNumber;
  }

  return normalizedNumber.startsWith('+972') ? normalizedNumber : null;
};

const getAppointmentPackageData = (appointment) => {
  const selectedServices = getSelectedServices(appointment);

  const packageService = selectedServices.find(
    (service) =>
      service?.isPackage === true ||
      service?.packageId != null ||
      service?.packageClientId != null
  );

  return {
    packageId:
      packageService?.packageId ??
      appointment?.packageId ??
      null,
    packageClientId:
      packageService?.packageClientId ??
      appointment?.packageClientId ??
      appointment?.clientPackageId ??
      appointment?.subscriptionId ??
      appointment?.clientSubscriptionId ??
      null,
  };
};

const getAppointmentDateTime = (appointment) => {
  if (!appointment?.dateOfAppointment) return null;

  const time = appointment.selectedHour || '00:00';
  const value = dayjs(`${dayjs(appointment.dateOfAppointment).format('YYYY-MM-DD')}T${time}`);

  return value.isValid() ? value : dayjs(appointment.dateOfAppointment);
};

const AppointmentsList = () => {
  const [loadedMasters, setLoadedMasters] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [expandedServices, setExpandedServices] = useState({});

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sessionAccount = useSelector((state) => state.userSession?.currentAccount);
  const currentUser = useSelector((state) => state.users?.currentUser);
  const user = sessionAccount || currentUser;

  const businessLocationsByUid = useSelector(
    (state) => state.businessLocation?.businessLocationsByUid || {}
  );
  const masters = useSelector((state) => state.master?.masters || EMPTY);
  const allAppointments = useSelector(
    (state) => state.appointments?.allAppointments || EMPTY
  );
  const clientPackages = useSelector(
    (state) =>
      state.clientPackages?.clientPackages ||
      state.clientPackages?.packages ||
      EMPTY
  );

  const [activeTab, setActiveTab] = useState('upcoming');
  const lang = i18n.language?.slice(0, 2) || 'ru';
  const isRTL = lang === 'he';

  const text = useMemo(() => {
    const dictionary = {
      ru: {
        subscription: 'Абонемент',
        sessionsLeft: 'сеанса осталось',
        sessionLeft: 'сеанс остался',
        lastSession: 'Последний сеанс',
        noVisitsYet: 'Посещений пока не было',
        nextMeeting: 'Следующая встреча',
        noNextMeeting: 'Следующая встреча пока не назначена',
        history: 'История посещений',
        used: 'Использовано',
        of: 'из',
        visit: 'Посещение',
        close: 'Закрыть',
        master: 'Мастер',
        showServices: 'Показать процедуры',
        hideServices: 'Скрыть процедуры',
      },
      he: {
        subscription: 'מנוי',
        sessionsLeft: 'מפגשים נותרו',
        sessionLeft: 'מפגש אחרון נותר',
        lastSession: 'המפגש האחרון',
        noVisitsYet: 'עדיין לא היו ביקורים',
        nextMeeting: 'הפגישה הבאה',
        noNextMeeting: 'עדיין לא נקבעה פגישה נוספת',
        history: 'היסטוריית ביקורים',
        used: 'נוצלו',
        of: 'מתוך',
        visit: 'ביקור',
        close: 'סגירה',
        master: 'בעל מקצוע',
        showServices: 'הצגת השירותים',
        hideServices: 'הסתרת השירותים',
      },
      en: {
        subscription: 'Membership',
        sessionsLeft: 'sessions left',
        sessionLeft: 'last session left',
        lastSession: 'Last session',
        noVisitsYet: 'No visits yet',
        nextMeeting: 'Next appointment',
        noNextMeeting: 'No upcoming appointment',
        history: 'Visit history',
        used: 'Used',
        of: 'of',
        visit: 'Visit',
        close: 'Close',
        master: 'Specialist',
        showServices: 'Show services',
        hideServices: 'Hide services',
      },
    };

    return dictionary[lang] || dictionary.ru;
  }, [lang]);

  const toggleServices = useCallback((appointmentKey) => {
    setExpandedServices((current) => ({
      ...current,
      [appointmentKey]: !current[appointmentKey],
    }));
  }, []);

  useEffect(() => {
    if (!user?.phone) return;
    dispatch(fetchAppointmentsByPhone(user.phone));
  }, [dispatch, user?.phone]);

  const filteredAppointments = useMemo(() => {
    const normalizedUserPhone = normalizephoneContact(user?.phone);
    if (!normalizedUserPhone) return EMPTY;

    const matchedAppointments = allAppointments.filter((appointment) => {
      const normalizedAppointmentPhone = normalizephoneContact(
        appointment?.phoneContact
      );

      return (
        normalizedAppointmentPhone &&
        normalizedUserPhone.includes(normalizedAppointmentPhone)
      );
    });

    // Убираем дубли одной и той же встречи из ответа API.
    const uniqueAppointments = new Map();

    matchedAppointments.forEach((appointment) => {
      const key = appointment?.id != null
        ? `id-${appointment.id}`
        : [
          appointment?.uid,
          appointment?.dateOfAppointment,
          appointment?.selectedHour,
          appointment?.phoneContact,
          appointment?.statusClient,
        ].join('|');

      if (!uniqueAppointments.has(key)) {
        uniqueAppointments.set(key, appointment);
      }
    });

    return Array.from(uniqueAppointments.values());
  }, [allAppointments, user?.phone]);

  const primaryUid = useMemo(() => {
    const sorted = [...filteredAppointments].sort((a, b) => {
      const dateA = getAppointmentDateTime(a)?.valueOf() || 0;
      const dateB = getAppointmentDateTime(b)?.valueOf() || 0;
      return dateB - dateA;
    });

    return sorted[0]?.uid || user?.uid || null;
  }, [filteredAppointments, user?.uid]);

  /*
   * ВАЖНО:
   * user.id / user.userId — это ID аккаунта авторизации кабинета,
   * а API абонементов ожидает ID клиента из таблицы клиентов мастера.
   * Поэтому сначала берём clientId именно из записи клиента.
   */
  const clientId = useMemo(() => {
    const appointmentWithClientId = filteredAppointments.find(
      (appointment) =>
        appointment?.clientId != null ||
        appointment?.client_id != null ||
        appointment?.contactId != null ||
        appointment?.contact_id != null
    );

    return (
      appointmentWithClientId?.clientId ??
      appointmentWithClientId?.client_id ??
      appointmentWithClientId?.contactId ??
      appointmentWithClientId?.contact_id ??
      user?.clientId ??
      user?.client_id ??
      null
    );
  }, [filteredAppointments, user?.clientId, user?.client_id]);


  useEffect(() => {

    dispatch(
      fetchCabinetClientPackages({
        phone: user.phone,
      })
    )
      .unwrap()
      .then((packages) => {
      })
      .catch((error) => {
      })
  }, [dispatch, user?.phone])

  useEffect(() => {
    if (!clientId) return;

    dispatch(
      fetchClientPackages({
        uid: primaryUid || filteredAppointments[0]?.uid || null,
        clientId,
      })
    )
      .unwrap()
      .catch((error) => {
        console.error('[AppointmentsList] fetchClientPackages failed', {
          clientId,
          uid: primaryUid,
          error,
        });
      });
  }, [dispatch, primaryUid, clientId, filteredAppointments]);

  useEffect(() => {
    const uniqueUids = [
      ...new Set(filteredAppointments.map((appointment) => appointment.uid).filter(Boolean)),
    ];

    uniqueUids.forEach((uid) => {
      if (!businessLocationsByUid[uid]) {
        dispatch(fetchBusinessLocationByUid(uid));
      }
    });
  }, [filteredAppointments, businessLocationsByUid, dispatch]);

  const loadMasterByUid = useCallback(
    async (uid) => {
      if (!uid || loadedMasters[uid]) return;

      try {
        const result = await dispatch(fetchPublicMaster(uid)).unwrap();
        setLoadedMasters((previous) => ({ ...previous, [uid]: result }));
      } catch {
        // Публичные данные мастера не должны ломать кабинет клиента.
      }
    },
    [loadedMasters, dispatch]
  );

  useEffect(() => {
    const uniqueUids = [
      ...new Set(filteredAppointments.map((appointment) => appointment.uid).filter(Boolean)),
    ];
    const uidsToFetch = uniqueUids.filter((uid) => !loadedMasters[uid]);

    uidsToFetch.forEach((uid) => loadMasterByUid(uid));
  }, [filteredAppointments, loadedMasters, loadMasterByUid]);

  useEffect(() => {
    if (!selectedPackage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedPackage(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedPackage]);

  const getStatus = useCallback((appointment) => {
    if (!appointment) return 'unknown';

    const { statusClient } = appointment;
    const appointmentDate = getAppointmentDateTime(appointment);
    const now = dayjs();

    if (statusClient === 'GroupSession') return 'unknown';
    if (statusClient === 'Canceled' || statusClient === 'NotArrived') return 'cancelled';
    if (statusClient === 'Arrived') return 'completed';

    if (statusClient === 'Confirmed') {
      return appointmentDate?.isBefore(now) ? 'completed' : 'upcoming';
    }

    if (appointmentDate?.isAfter(now)) return 'upcoming';
    if (appointmentDate?.isBefore(now)) return 'completed';

    return 'unknown';
  }, []);

  const formatDate = useCallback(
    (dateString, withWeekday = true) => {
      if (!dateString) return '-';

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';

      const localeMap = {
        ru: 'ru-RU',
        he: 'he-IL',
        en: 'en-GB',
      };

      const result = date.toLocaleDateString(localeMap[lang] || 'ru-RU', {
        ...(withWeekday ? { weekday: 'long' } : {}),
        day: 'numeric',
        month: 'long',
        year: withWeekday ? undefined : 'numeric',
      });

      return result.charAt(0).toUpperCase() + result.slice(1);
    },
    [lang]
  );

  const formatAppointmentLine = useCallback(
    (appointment) => {
      if (!appointment) return '';

      return `${formatDate(appointment.dateOfAppointment)}, ${appointment.selectedHour || '-'
        }`;
    },
    [formatDate]
  );

  const findMasterByUid = useCallback(
    (uid) =>
      masters.find((master) => String(master?.uid) === String(uid)) ||
      loadedMasters[uid],
    [masters, loadedMasters]
  );

  const getServiceNames = useCallback(
    (appointment, type = 'all') => {
      const names = getSelectedServices(appointment)
        .filter((service) => {
          if (type === 'primary') return !service?.isUpsell;
          if (type === 'upsell') return !!service?.isUpsell;
          return true;
        })
        .map((service) => {
          const localizedNames = {
            ru: service?.serviceNameRu || service?.nameRu,
            he: service?.serviceNameHe || service?.nameHe,
            en: service?.serviceNameEn || service?.nameEn,
          };

          const name = (
            localizedNames[lang] ||
            service?.serviceName ||
            service?.name ||
            ''
          );
          const quantity = Math.max(1, Number(service?.quantity || 1));

          return name && quantity > 1 ? `${name} ×${quantity}` : name;
        })
        .filter(Boolean);

      if (names.length > 0) {
        return names;
      }

      if (type !== 'all') return EMPTY;

      const appointmentNames = {
        ru: appointment?.serviceNameRu,
        he: appointment?.serviceNameHe,
        en: appointment?.serviceNameEn,
      };

      const fallbackName =
        appointmentNames[lang] || appointment?.serviceName || '';

      return fallbackName ? [fallbackName] : EMPTY;
    },
    [lang]
  );

  const getServiceName = useCallback(
    (appointment) => getServiceNames(appointment).join(', '),
    [getServiceNames]
  );

  const getServicesCountLabel = useCallback(
    (count) => {
      if (lang === 'he') {
        return count === 1 ? 'שירות אחד' : `${count} שירותים`;
      }

      if (lang === 'en') {
        return `${count} ${count === 1 ? 'service' : 'services'}`;
      }

      const mod10 = count % 10;
      const mod100 = count % 100;

      if (mod10 === 1 && mod100 !== 11) {
        return `${count} процедура`;
      }

      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return `${count} процедуры`;
      }

      return `${count} процедур`;
    },
    [lang]
  );

  const activeSessionPackages = useMemo(() => {
    const now = dayjs();

    return clientPackages.filter((item) => {
      const total = Number(item?.totalSessions || 0);
      const used = Number(item?.usedSessions || 0);
      const remaining = Math.max(total - used, 0);
      const expiryDate = item?.expiryDate ? dayjs(item.expiryDate) : null;
      const status = String(item?.status || 'active').toLowerCase();

      const packageType = String(item?.packageType || 'sessions').toLowerCase();

      return (
        ['sessions', 'session', 'visits', 'appointments'].includes(packageType) &&
        total > 0 &&
        remaining > 0 &&
        !['expired', 'cancelled', 'completed'].includes(status) &&
        (!expiryDate || !expiryDate.isBefore(now, 'day'))
      );
    });
  }, [clientPackages]);

  const packageCards = useMemo(() => {
    return activeSessionPackages.map((pkg) => {
      const clientPackageId = String(pkg.id);
      const sourcePackageId = String(pkg.packageId);
      const total = Number(pkg.totalSessions || 0);
      const used = Number(pkg.usedSessions || 0);
      const remaining = Math.max(total - used, 0);

      const linkedAppointments = filteredAppointments
        .filter((appointment) => {
          const {
            packageId: appointmentPackageId,
            packageClientId: appointmentPackageClientId,
          } = getAppointmentPackageData(appointment);

          if (appointmentPackageClientId != null) {
            return String(appointmentPackageClientId) === clientPackageId;
          }

          if (appointmentPackageId != null && pkg.packageId != null) {
            return String(appointmentPackageId) === sourcePackageId;
          }

          /*
           * Запасной вариант для старых записей, где packageId ещё не сохранялся:
           * связываем запись по названию услуги и названию абонемента.
           */
          const serviceNames = getServiceNames(appointment).map((name) =>
            String(name).trim().toLowerCase()
          );
          const packageName = String(
            lang === "he"
              ? pkg.packageNameHe || pkg.packageName
              : lang === "en"
                ? pkg.packageNameEn || pkg.packageName
                : pkg.packageName || ""
          )
            .trim()
            .toLowerCase();

          return Boolean(
            packageName && serviceNames.some((serviceName) => serviceName === packageName)
          );
        })
        .sort((a, b) => {
          const dateA = getAppointmentDateTime(a)?.valueOf() || 0;
          const dateB = getAppointmentDateTime(b)?.valueOf() || 0;
          return dateB - dateA;
        });

      const history = linkedAppointments.filter(
        (appointment) => getStatus(appointment) === 'completed'
      );

      const upcoming = linkedAppointments
        .filter((appointment) => getStatus(appointment) === 'upcoming')
        .sort((a, b) => {
          const dateA = getAppointmentDateTime(a)?.valueOf() || 0;
          const dateB = getAppointmentDateTime(b)?.valueOf() || 0;
          return dateA - dateB;
        });

      return {
        pkg,
        total,
        used,
        remaining,
        history,
        linkedAppointments,
        lastAppointment: history[0] || null,
        nextAppointment: upcoming[0] || null,
      };
    });
  }, [
    activeSessionPackages,
    filteredAppointments,
    getServiceNames,
    getStatus,
    lang,
  ]);

  const packageAppointmentIds = useMemo(() => {
    const ids = new Set();

    packageCards.forEach((card) => {
      card.linkedAppointments.forEach((appointment) => {
        if (appointment?.id != null) {
          ids.add(String(appointment.id));
        }
      });
    });

    return ids;
  }, [packageCards]);

  const filteredByTab = useMemo(
    () =>
      filteredAppointments.filter((appointment) => {
        const status = getStatus(appointment);

        if (status === 'unknown' || status !== activeTab) {
          return false;
        }

        const isPackageAppointment =
          appointment?.id != null &&
          packageAppointmentIds.has(String(appointment.id));

        return !isPackageAppointment;
      }),
    [filteredAppointments, activeTab, getStatus, packageAppointmentIds]
  );

  const calculateEndTime = (
    selectedHour,
    selectedDuration,
    breakNewAppointment
  ) => {
    if (!selectedHour || !selectedDuration || !breakNewAppointment) return null;

    const [startHour, startMinute] = selectedHour.split(':').map(Number);
    const [durationHour, durationMinute] = selectedDuration.split(':').map(Number);
    const [breakHour, breakMinute] = breakNewAppointment.split(':').map(Number);

    let endHour = startHour + durationHour + breakHour;
    let endMinute = startMinute + durationMinute + breakMinute;

    if (endMinute >= 60) {
      endHour += Math.floor(endMinute / 60);
      endMinute %= 60;
    }

    return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  };

  const generateGoogleCalendarLink = (appointment, businessLocation) => {
    if (
      !appointment.selectedHour ||
      !appointment.selectedDuration ||
      !appointment.breakNewAppointment
    ) {
      return null;
    }

    const [startHour, startMinute] = appointment.selectedHour.split(':').map(Number);
    const startDate = dayjs(appointment.dateOfAppointment)
      .set('hour', startHour)
      .set('minute', startMinute)
      .format('YYYYMMDDTHHmmss');

    const endHour = calculateEndTime(
      appointment.selectedHour,
      appointment.selectedDuration,
      appointment.breakNewAppointment
    );

    if (!endHour) return null;

    const [endHourValue, endMinuteValue] = endHour.split(':').map(Number);
    const endDate = dayjs(appointment.dateOfAppointment)
      .set('hour', endHourValue)
      .set('minute', endMinuteValue)
      .format('YYYYMMDDTHHmmss');

    const serviceName = getServiceName(appointment);
    const clientName = `${appointment.firstNameContact || ''} ${appointment.lastNameContact || ''
      }`.trim();
    const price = getAppointmentTotalPrice(appointment);
    const address = businessLocation
      ? `${businessLocation.city}, ${businessLocation.address}`
      : '';

    const localeMap = {
      ru: {
        client: 'Клиент',
        duration: 'Длительность',
        price: 'Стоимость',
        currency: '₪',
      },
      he: {
        client: 'לקוח',
        duration: 'משך',
        price: 'מחיר',
        currency: '₪',
      },
      en: {
        client: 'Client',
        duration: 'Duration',
        price: 'Price',
        currency: '₪',
      },
    };

    const locale = localeMap[lang] || localeMap.ru;
    const notes = [
      `${locale.client}: ${clientName}`,
      `${locale.duration}: ${appointment.selectedDuration}`,
      price ? `${locale.price}: ${price} ${locale.currency}` : null,
      address || null,
    ]
      .filter(Boolean)
      .join('\n');

    return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      serviceName
    )}&dates=${startDate}/${endDate}&details=${encodeURIComponent(
      notes
    )}&location=${encodeURIComponent(address)}`;
  };

  const getTokenForMaster = async (uid) => {
    try {
      const result = await dispatch(fetchPushToken(uid)).unwrap();
      return result?.token;
    } catch {
      return null;
    }
  };

  const handleRebook = async (masterLink) => {
    if (!masterLink) return;

    const path = String(masterLink).replace(/^\/+/, '');
    let url = `https://zmanup.co.il/${lang}/${path}?from=cabinet`;

    try {
      const handoffToken = await dispatch(createBookingHandoff()).unwrap();
      if (handoffToken) url += `&ct=${handoffToken}`;
    } catch {
      // Даже без handoff клиент сможет перейти к записи.
    }

    window.location.assign(url);
  };

  const handleUpdateStatus = async (appointment, newStatus) => {
    const master =
      masters.find((item) => item?.uid === appointment?.uid) ||
      loadedMasters[appointment?.uid];

    try {
      await dispatch(
        updateAppointment({
          id: appointment.id,
          appointment: { statusClient: newStatus },
        })
      ).unwrap();

      if (master) {
        const masterLanguage = master.language || 'he';

        const notificationMessages = {
          Canceled: {
            he: () =>
              `📅 הלקוח ${appointment.firstNameContact}${appointment.lastNameContact
                ? ` ${appointment.lastNameContact}`
                : ''
              } ביטל/ה את הפגישה ליום ${dayjs(
                appointment.dateOfAppointment
              ).format('DD.MM.YYYY')} בשעה ${appointment.selectedHour}.`,
            en: () =>
              `📅 The client ${appointment.firstNameContact}${appointment.lastNameContact
                ? ` ${appointment.lastNameContact}`
                : ''
              } has canceled their appointment on ${dayjs(
                appointment.dateOfAppointment
              ).format('MMMM DD, YYYY')} at ${appointment.selectedHour}.`,
            ru: () =>
              `📅 Клиент ${appointment.firstNameContact}${appointment.lastNameContact
                ? ` ${appointment.lastNameContact}`
                : ''
              } отменил(а) встречу на ${dayjs(
                appointment.dateOfAppointment
              ).format('DD.MM.YYYY')} в ${appointment.selectedHour}.`,
          },
          Confirmed: {
            he: () =>
              `הלקוח ${appointment.firstNameContact}${appointment.lastNameContact
                ? ` ${appointment.lastNameContact}`
                : ''
              } מאשר את הפגישה בתאריך ${dayjs(
                appointment.dateOfAppointment
              ).format('DD.MM.YYYY')} בשעה ${appointment.selectedHour}. 🎉`,
            en: () =>
              `The client ${appointment.firstNameContact}${appointment.lastNameContact
                ? ` ${appointment.lastNameContact}`
                : ''
              } confirms the appointment on ${dayjs(
                appointment.dateOfAppointment
              ).format('MMMM DD, YYYY')} at ${appointment.selectedHour}. 🎉`,
            ru: () =>
              `Клиент ${appointment.firstNameContact}${appointment.lastNameContact
                ? ` ${appointment.lastNameContact}`
                : ''
              } подтвердил(а) встречу на ${dayjs(
                appointment.dateOfAppointment
              ).format('DD.MM.YYYY')} в ${appointment.selectedHour}. 🎉`,
          },
        };

        const notificationBody =
          notificationMessages[newStatus]?.[masterLanguage]?.() ??
          notificationMessages[newStatus]?.en?.();

        const notificationTitle =
          newStatus === 'Canceled'
            ? masterLanguage === 'he'
              ? 'ביטול הפגישה'
              : masterLanguage === 'ru'
                ? 'Отмена встречи'
                : 'Appointment Canceled'
            : masterLanguage === 'he'
              ? 'אישור הפגישה'
              : masterLanguage === 'ru'
                ? 'Подтверждение встречи'
                : 'Appointment Confirmed';

        const masterPushToken = await getTokenForMaster(appointment.uid);

        if (masterPushToken) {
          await dispatch(
            sendPushNotification({
              token: masterPushToken,
              title: 'ZmanUp',
              body: notificationBody,
              screen: 'Notification',
            })
          ).unwrap();
        }

        await dispatch(
          addNotification({
            uid: appointment.uid,
            notification: {
              type: newStatus === 'Canceled' ? 'warning' : 'success',
              title: notificationTitle,
              message: notificationBody,
              is_read: false,
              created_at: new Date().toISOString(),
            },
          })
        ).unwrap();
      }

      await dispatch(fetchAppointmentsByPhone(user.phone));
    } catch (error) {
      console.error('[handleUpdateStatus]', error);
    }
  };

  const renderPackageCard = (item) => {
    const packageNames = {
      ru:
        item.pkg.packageNameRu ||
        item.pkg.nameRu,

      he:
        item.pkg.packageNameHe ||
        item.pkg.nameHe,

      en:
        item.pkg.packageNameEn ||
        item.pkg.nameEn,
    };

    const packageName =
      lang === "he"
        ? item.pkg.packageNameHe || item.pkg.packageName
        : lang === "en"
          ? item.pkg.packageNameEn || item.pkg.packageName
          : item.pkg.packageName;

    return (
      <button
        type="button"
        key={item.pkg.id}
        className="cabinet-package-card"
        onClick={() => setSelectedPackage(item)}
      >
        <span className="cabinet-package-card__top">
          <span>
            <span className="cabinet-package-card__eyebrow">
              {text.subscription}
            </span>
            <span className="cabinet-package-card__name">
              {packageName}
            </span>
          </span>

          <span className="cabinet-package-card__arrow" aria-hidden="true">
            {isRTL ? '‹' : '›'}
          </span>
        </span>

        <span className="cabinet-package-card__remaining">
          <strong>{item.remaining}</strong>
          <span>
            {item.remaining === 1 ? text.sessionLeft : text.sessionsLeft}
          </span>
        </span>

        <span className="cabinet-package-card__progress" aria-hidden="true">
          {Array.from({ length: item.total }).map((_, index) => (
            <span
              key={index}
              className={
                index < item.used
                  ? 'cabinet-package-card__dot cabinet-package-card__dot--used'
                  : 'cabinet-package-card__dot'
              }
            />
          ))}
        </span>

        <span className="cabinet-package-card__meta-grid">
          <span className="cabinet-package-card__meta">
            <span className="cabinet-package-card__meta-label">
              {text.lastSession}
            </span>
            <span className="cabinet-package-card__meta-value">
              {item.lastAppointment
                ? formatAppointmentLine(item.lastAppointment)
                : text.noVisitsYet}
            </span>
          </span>

          <span className="cabinet-package-card__divider" />

          <span className="cabinet-package-card__meta">
            <span className="cabinet-package-card__meta-label">
              {text.nextMeeting}
            </span>
            <span className="cabinet-package-card__meta-value">
              {item.nextAppointment
                ? formatAppointmentLine(item.nextAppointment)
                : text.noNextMeeting}
            </span>
          </span>
        </span>
      </button>
    );
  };

  return (
    <div className="appointments-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <style>{`
        .cabinet-packages {
          display: grid;
          gap: 14px;
          margin: 0 0 20px;
        }

        .cabinet-package-card {
          width: 100%;
          border: 1px solid rgba(38, 38, 79, 0.08);
          border-radius: 24px;
          padding: 20px;
          text-align: inherit;
          color: #26264F;
          background:
            radial-gradient(circle at 90% 0%, rgba(196, 230, 229, 0.72), transparent 38%),
            linear-gradient(145deg, #FFFFFF 0%, #FAFCFC 100%);
          box-shadow:
            0 14px 38px rgba(38, 38, 79, 0.08),
            0 2px 8px rgba(38, 38, 79, 0.04);
          cursor: pointer;
          transition: transform 180ms ease, box-shadow 180ms ease;
          appearance: none;
          font: inherit;
        }

        .cabinet-package-card:hover {
          transform: translateY(-2px);
          box-shadow:
            0 18px 44px rgba(38, 38, 79, 0.11),
            0 4px 10px rgba(38, 38, 79, 0.05);
        }

        .cabinet-package-card:active {
          transform: scale(0.99);
        }

        .cabinet-package-card__top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }

        .cabinet-package-card__eyebrow,
        .cabinet-package-card__name,
        .cabinet-package-card__remaining,
        .cabinet-package-card__meta,
        .cabinet-package-card__meta-label,
        .cabinet-package-card__meta-value {
          display: block;
        }

        .cabinet-package-card__eyebrow {
          margin-bottom: 5px;
          color: #77778B;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .cabinet-package-card__name {
          font-size: 19px;
          font-weight: 750;
          line-height: 1.25;
        }

        .cabinet-package-card__arrow {
          display: grid;
          place-items: center;
          width: 32px;
          height: 32px;
          flex: 0 0 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.76);
          color: #26264F;
          font-size: 25px;
          line-height: 1;
          box-shadow: 0 3px 10px rgba(38, 38, 79, 0.08);
        }

        .cabinet-package-card__remaining {
          display: flex;
          align-items: baseline;
          gap: 10px;
          margin: 22px 0 14px;
        }

        .cabinet-package-card__remaining strong {
          font-size: clamp(42px, 8vw, 58px);
          font-weight: 760;
          line-height: 0.9;
          letter-spacing: -0.06em;
        }

        .cabinet-package-card__remaining span {
          color: #66667A;
          font-size: 14px;
          font-weight: 650;
        }

        .cabinet-package-card__progress {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 20px;
        }

        .cabinet-package-card__dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(38, 38, 79, 0.12);
        }

        .cabinet-package-card__dot--used {
          background: #26264F;
        }

        .cabinet-package-card__meta-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
          gap: 14px;
          padding-top: 16px;
          border-top: 1px solid rgba(38, 38, 79, 0.08);
        }

        .cabinet-package-card__divider {
          width: 1px;
          background: rgba(38, 38, 79, 0.08);
        }

        .cabinet-package-card__meta-label {
          margin-bottom: 6px;
          color: #888897;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .cabinet-package-card__meta-value {
          color: #26264F;
          font-size: 13px;
          font-weight: 650;
          line-height: 1.4;
        }

        .cabinet-package-modal {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 20px;
          background: rgba(20, 20, 36, 0.44);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .cabinet-package-modal__sheet {
          width: min(100%, 560px);
          max-height: min(82vh, 760px);
          overflow: hidden;
          border-radius: 30px;
          background: #F7F7FA;
          box-shadow: 0 30px 90px rgba(20, 20, 36, 0.28);
          animation: cabinetPackageModalIn 220ms ease-out;
        }

        @keyframes cabinetPackageModalIn {
          from {
            opacity: 0;
            transform: translateY(24px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .cabinet-package-modal__header {
          position: sticky;
          top: 0;
          z-index: 2;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 22px 22px 16px;
          background: rgba(247, 247, 250, 0.9);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .cabinet-package-modal__title {
          margin: 0;
          color: #26264F;
          font-size: 22px;
          line-height: 1.2;
        }

        .cabinet-package-modal__subtitle {
          margin: 6px 0 0;
          color: #737386;
          font-size: 14px;
          font-weight: 600;
        }

        .cabinet-package-modal__close {
          display: grid;
          place-items: center;
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          background: #E7E7EC;
          color: #4D4D62;
          font-size: 22px;
          cursor: pointer;
        }

        .cabinet-package-modal__content {
          max-height: calc(min(82vh, 760px) - 90px);
          overflow-y: auto;
          padding: 0 18px 24px;
        }

        .cabinet-package-modal__summary {
          margin-bottom: 16px;
          padding: 20px;
          border-radius: 22px;
          background: linear-gradient(145deg, #C4E6E5, #D9E5F1);
          color: #26264F;
        }

        .cabinet-package-modal__number {
          display: block;
          font-size: 44px;
          font-weight: 780;
          line-height: 1;
          letter-spacing: -0.05em;
        }

        .cabinet-package-modal__summary-label {
          display: block;
          margin-top: 6px;
          font-size: 14px;
          font-weight: 700;
        }

        .cabinet-package-modal__next {
          margin-top: 16px;
          padding-top: 15px;
          border-top: 1px solid rgba(38, 38, 79, 0.12);
        }

        .cabinet-package-modal__next-label {
          display: block;
          margin-bottom: 5px;
          color: rgba(38, 38, 79, 0.62);
          font-size: 11px;
          font-weight: 750;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .cabinet-package-modal__next-value {
          display: block;
          font-size: 15px;
          font-weight: 750;
        }

        .cabinet-package-modal__section-title {
          margin: 22px 4px 10px;
          color: #77778B;
          font-size: 12px;
          font-weight: 750;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .cabinet-package-modal__history {
          overflow: hidden;
          border-radius: 20px;
          background: #FFFFFF;
          box-shadow: 0 6px 18px rgba(38, 38, 79, 0.05);
        }

        .cabinet-package-modal__visit {
          display: flex;
          align-items: center;
          gap: 13px;
          padding: 15px 16px;
        }

        .cabinet-package-modal__visit + .cabinet-package-modal__visit {
          border-top: 1px solid #EEEEF2;
        }

        .cabinet-package-modal__visit-index {
          display: grid;
          place-items: center;
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          border-radius: 50%;
          background: #EDF5F4;
          color: #26264F;
          font-size: 13px;
          font-weight: 800;
        }

        .cabinet-package-modal__visit-body {
          min-width: 0;
          flex: 1;
        }

        .cabinet-package-modal__visit-date,
        .cabinet-package-modal__visit-service,
        .cabinet-package-modal__visit-master {
          display: block;
        }

        .cabinet-package-modal__visit-date {
          color: #26264F;
          font-size: 14px;
          font-weight: 750;
        }

        .cabinet-package-modal__visit-service {
          margin-top: 3px;
          color: #747486;
          font-size: 13px;
          font-weight: 600;
        }

        .cabinet-package-modal__visit-master {
          margin-top: 2px;
          color: #9A9AA7;
          font-size: 12px;
        }

        .cabinet-package-modal__empty {
          padding: 24px 18px;
          color: #7C7C8E;
          text-align: center;
          font-size: 14px;
          font-weight: 600;
        }

        @media (min-width: 700px) {
          .cabinet-package-modal {
            align-items: center;
          }
        }

        @media (max-width: 520px) {
          .cabinet-package-card {
            padding: 18px;
            border-radius: 22px;
          }

          .cabinet-package-card__meta-grid {
            grid-template-columns: 1fr;
          }

          .cabinet-package-card__divider {
            display: none;
          }

          .cabinet-package-card__meta + .cabinet-package-card__meta {
            padding-top: 13px;
            border-top: 1px solid rgba(38, 38, 79, 0.08);
          }

          .cabinet-package-modal {
            padding: 0;
          }

          .cabinet-package-modal__sheet {
            width: 100%;
            max-height: 88vh;
            border-radius: 28px 28px 0 0;
          }
        }
      `}</style>

      <h2>{t('your_appointments')}</h2>

      <div className="appointments-tabs">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
        >
          {t('upcoming')}
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
        >
          {t('past')}
        </button>
        <button
          onClick={() => setActiveTab('cancelled')}
          className={`tab-button ${activeTab === 'cancelled' ? 'active' : ''}`}
        >
          {t('appointment_status_cancelled')}
        </button>
      </div>

      {activeTab === 'upcoming' && packageCards.length > 0 && (
        <div className="cabinet-packages">
          {packageCards.map(renderPackageCard)}
        </div>
      )}

      <div className="appointments-list">
        {filteredByTab.length > 0 ? (
          filteredByTab.map((appointment, index) => {
            const master = findMasterByUid(appointment?.uid);
            const status = getStatus(appointment);
            const serviceNames = getServiceNames(appointment, 'primary');
            const upsellServiceNames = getServiceNames(appointment, 'upsell');
            const hasPrimaryServices = serviceNames.length > 0;
            const displayedServiceNames = hasPrimaryServices
              ? serviceNames
              : getServiceNames(appointment);
            const upsellLabel = lang === 'he'
              ? 'תוספות'
              : lang === 'en'
                ? 'Add-ons'
                : 'Дополнительно';
            const appointmentKey = appointment?.id != null
              ? String(appointment.id)
              : `${appointment?.uid || ''}-${appointment?.dateOfAppointment || ''}-${appointment?.selectedHour || ''}-${index}`;
            const areServicesExpanded = Boolean(expandedServices[appointmentKey]);
            const businessLocation = businessLocationsByUid[appointment.uid];
            const googleCalendarLink = generateGoogleCalendarLink(
              appointment,
              businessLocation
            );

            return (
              <div
                key={appointment.id || index}
                className={`appt-card ${status === 'completed' ? 'appt-card--dim' : ''
                  }`}
              >
                <div
                  className="appt-card__bar"
                  style={{
                    background:
                      status === 'upcoming'
                        ? 'linear-gradient(90deg,#d5edbe,#c4e6e5,#4e9e8a)'
                        : status === 'completed'
                          ? 'linear-gradient(90deg,#c4e6e5,#a5d7d6)'
                          : 'linear-gradient(90deg,#ffe0db,#f5c6c0)',
                  }}
                />

                <div
                  className="appt-card__head"
                  style={displayedServiceNames.length > 1 || upsellServiceNames.length > 0
                    ? {
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      columnGap: '10px',
                      rowGap: '10px',
                      alignItems: 'start',
                    }
                    : undefined}
                >
                  {displayedServiceNames.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => toggleServices(appointmentKey)}
                      aria-expanded={areServicesExpanded}
                      aria-label={areServicesExpanded
                        ? text.hideServices
                        : text.showServices}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '7px',
                        minWidth: 0,
                        padding: 0,
                        border: 0,
                        background: 'transparent',
                        color: 'inherit',
                        font: 'inherit',
                        textAlign: 'start',
                        cursor: 'pointer',
                      }}
                    >
                      <span className="appt-card__svc" style={{ minWidth: 0 }}>
                        {getServicesCountLabel(displayedServiceNames.length)}
                      </span>
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        style={{
                          flex: '0 0 18px',
                          width: '18px',
                          height: '18px',
                          fill: 'none',
                          stroke: 'currentColor',
                          strokeWidth: 2,
                          strokeLinecap: 'round',
                          strokeLinejoin: 'round',
                          transform: areServicesExpanded
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 180ms ease',
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  ) : (
                    <span className="appt-card__svc" style={{ minWidth: 0 }}>
                      {displayedServiceNames.join(', ')}
                    </span>
                  )}
                  <span
                    className={`appt-card__badge appt-card__badge--${status}`}
                    style={displayedServiceNames.length > 1
                      ? { justifySelf: 'end', flexShrink: 0 }
                      : undefined}
                  >
                    <span className="appt-card__badge-dot" />
                    {status === 'upcoming'
                      ? t('scheduled')
                      : status === 'completed'
                        ? t('completed')
                        : t('cancelled_status')}
                  </span>

                  {displayedServiceNames.length > 1 && areServicesExpanded && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        display: 'grid',
                        gap: '6px',
                        width: '100%',
                      }}
                    >
                      {displayedServiceNames.map((serviceName, serviceIndex) => (
                        <div
                          key={`${serviceName}-${serviceIndex}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '9px',
                            minWidth: 0,
                            padding: '8px 10px',
                            border: '1px solid rgba(78, 158, 138, 0.16)',
                            borderRadius: '10px',
                            background: '#f5faf8',
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flex: '0 0 22px',
                              width: '22px',
                              height: '22px',
                              borderRadius: '50%',
                              background: '#dcefe8',
                              color: '#2d7063',
                              fontSize: '11px',
                              fontWeight: 700,
                            }}
                          >
                            {serviceIndex + 1}
                          </span>
                          <span
                            style={{
                              minWidth: 0,
                              color: '#254f48',
                              fontSize: '13px',
                              fontWeight: 600,
                              lineHeight: 1.35,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {serviceName}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {upsellServiceNames.length > 0 && hasPrimaryServices && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        color: '#9A5A48',
                        fontSize: '12px',
                        fontWeight: 600,
                        marginTop: '-2px',
                      }}
                    >
                      ＋ {upsellLabel}: {upsellServiceNames.join(', ')}
                    </div>
                  )}
                </div>

                <div className="appt-card__details">
                  {[
                    {
                      icon: (
                        <svg viewBox="0 0 24 24">
                          <rect x="3" y="4" width="18" height="18" rx="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      ),
                      label: t('date_and_time'),
                      value: `${formatDate(
                        appointment.dateOfAppointment
                      )}, ${appointment.selectedHour || '-'}`,
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                        </svg>
                      ),
                      label: t('master'),
                      value: master
                        ? `${master.firstName || ''} ${master.lastName || ''}`.trim()
                        : t('master_not_found'),
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      ),
                      label: t('duration'),
                      value: appointment.selectedDuration || '-',
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24">
                          <rect x="2" y="6" width="20" height="12" rx="2" />
                          <circle cx="12" cy="12" r="2" />
                        </svg>
                      ),
                      label: t('price'),
                      value: `${getAppointmentTotalPrice(appointment)} ₪`,
                    },
                    {
                      icon: (
                        <svg viewBox="0 0 24 24">
                          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                      ),
                      label: t('address'),
                      value: businessLocation
                        ? `${businessLocation.city}, ${businessLocation.address}`
                        : t('address_not_found'),
                    },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="appt-card__detail-row">
                      <div className="appt-card__detail-icon">{icon}</div>
                      <div className="appt-card__detail-text">
                        <span className="appt-card__detail-lbl">{label}</span>
                        <span className="appt-card__detail-val">{value}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="appt-card__actions">
                  {status === 'upcoming' && (
                    <>
                      <div className="appt-card__actions-row">
                        <button
                          className="appt-btn appt-btn--confirm"
                          onClick={() =>
                            handleUpdateStatus(appointment, 'Confirmed')
                          }
                          disabled={appointment.statusClient === 'Confirmed'}
                        >
                          ✓ {t('confirm')}
                        </button>
                        <button
                          className="appt-btn appt-btn--cancel"
                          onClick={() =>
                            handleUpdateStatus(appointment, 'Canceled')
                          }
                        >
                          ✕ {t('cancel')}
                        </button>
                      </div>

                      {googleCalendarLink && (
                        <a
                          href={googleCalendarLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="appt-btn appt-btn--google"
                        >
                          <FcGoogle size={18} />
                          {t('add_to_google_calendar')}
                        </a>
                      )}
                    </>
                  )}

                  {status === 'completed' && (
                    <div className="appt-card__actions-row">
                      <button
                        className="appt-btn appt-btn--confirm"
                        disabled={appointment.statusClient === 'Canceled'}
                        style={{
                          opacity:
                            appointment.statusClient === 'Canceled' ? 0.5 : 1,
                        }}
                        onClick={() =>
                          master?.masterLink &&
                          navigate(`/${lang}/${master.masterLink}/ReviewForm`)
                        }
                      >
                        ★ {t('leave_review')}
                      </button>

                      {master?.masterLink && (
                        <button
                          className="appt-btn appt-btn--rebook"
                          onClick={() => handleRebook(master.masterLink)}
                        >
                          ↻ {t('book_now')}
                        </button>
                      )}
                    </div>
                  )}

                  {status === 'cancelled' && master?.masterLink && (
                    <div className="appt-card__actions-row">
                      <button
                        className="appt-btn appt-btn--rebook"
                        onClick={() => handleRebook(master.masterLink)}
                      >
                        ↻ {t('book_now')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p>{t('no_records_in_this_category')}</p>
        )}
      </div>

      <QuickBookFAB loadedMasters={loadedMasters} />

      {selectedPackage && (
        <div
          className="cabinet-package-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedPackage(null);
            }
          }}
        >
          <section
            className="cabinet-package-modal__sheet"
            role="dialog"
            aria-modal="true"
            aria-label={text.history}
          >
            <div className="cabinet-package-modal__header">
              <div>
                <h3 className="cabinet-package-modal__title">
                  {({
                    ru:
                      selectedPackage.pkg.packageNameRu ||
                      selectedPackage.pkg.nameRu,

                    he:
                      selectedPackage.pkg.packageNameHe ||
                      selectedPackage.pkg.nameHe,

                    en:
                      selectedPackage.pkg.packageNameEn ||
                      selectedPackage.pkg.nameEn,
                  }[lang]) ||
                    selectedPackage.pkg.packageName ||
                    selectedPackage.pkg.name ||
                    text.subscription}
                </h3>
                <p className="cabinet-package-modal__subtitle">
                  {text.used} {selectedPackage.used} {text.of}{' '}
                  {selectedPackage.total}
                </p>
              </div>

              <button
                type="button"
                className="cabinet-package-modal__close"
                onClick={() => setSelectedPackage(null)}
                aria-label={text.close}
              >
                ×
              </button>
            </div>

            <div className="cabinet-package-modal__content">
              <div className="cabinet-package-modal__summary">
                <span className="cabinet-package-modal__number">
                  {selectedPackage.remaining}
                </span>
                <span className="cabinet-package-modal__summary-label">
                  {selectedPackage.remaining === 1
                    ? text.sessionLeft
                    : text.sessionsLeft}
                </span>

                <div className="cabinet-package-modal__next">
                  <span className="cabinet-package-modal__next-label">
                    {text.nextMeeting}
                  </span>
                  <span className="cabinet-package-modal__next-value">
                    {selectedPackage.nextAppointment
                      ? formatAppointmentLine(
                        selectedPackage.nextAppointment
                      )
                      : text.noNextMeeting}
                  </span>
                </div>
              </div>

              <div className="cabinet-package-modal__section-title">
                {text.history}
              </div>

              <div className="cabinet-package-modal__history">
                {selectedPackage.history.length > 0 ? (
                  selectedPackage.history.map((appointment, historyIndex) => {
                    const master = findMasterByUid(appointment.uid);

                    return (
                      <div
                        key={appointment.id || historyIndex}
                        className="cabinet-package-modal__visit"
                      >
                        <span className="cabinet-package-modal__visit-index">
                          {selectedPackage.used - historyIndex}
                        </span>

                        <span className="cabinet-package-modal__visit-body">
                          <span className="cabinet-package-modal__visit-date">
                            {formatAppointmentLine(appointment)}
                          </span>
                          <span className="cabinet-package-modal__visit-service">
                            {getServiceName(appointment) ||
                              `${text.visit} ${selectedPackage.used - historyIndex
                              }`}
                          </span>

                          {master && (
                            <span className="cabinet-package-modal__visit-master">
                              {text.master}:{' '}
                              {`${master.firstName || ''} ${master.lastName || ''
                                }`.trim()}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="cabinet-package-modal__empty">
                    {text.noVisitsYet}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AppointmentsList;
